import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

@Injectable({ providedIn: 'root' })
export class TypeOfTaxpayerService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * LISTA: Tipo de contribuyentes (server-side pagination)
   */
  getTypeOfTaxpayers(
    countryId: string,
    page = 0,
    size = 10,
    sort = 'name',
    direction: 'asc' | 'desc' = 'asc',
    search?: string,
    status?: string,
  ): Observable<TypeOfTaxpayerPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }

    const url = `${this.apiUrl}/${countryId}/type-of-taxpayers`;
    return this.httpClient.get<ApiResponse<PageableData>>(url, { params }).pipe(
      map((response) => {
        const pageableData = response.data;
        const items = pageableData?.content || [];
        return {
          items: items.map((it: any) => this.mapItem(it)),
          page: pageableData?.page ?? 0,
          size: pageableData?.size ?? size,
          totalElements: pageableData?.totalElements ?? 0,
          totalPages: pageableData?.totalPages ?? 0,
        } as TypeOfTaxpayerPageResponse;
      }),
    );
  }

  getTypeOfTaxpayerById(countryId: string, id: string | number) {
    const url = `${this.apiUrl}/${countryId}/type-of-taxpayers/${id}`;
    return this.httpClient.get<ApiResponse<any>>(url).pipe(
      map((resp) => this.mapItem(resp.data)),
    );
  }

  private mapItem(it: any): TypeOfTaxpayer {
    const typePeople: TypeOfTaxpayerPeople[] = Array.isArray(it?.typePeople)
      ? it.typePeople
          .filter((tp: any) => tp?.id != null)
          .map((tp: any) => ({
            id: String(tp.id),
            prefix: tp.prefix || '',
            name: tp.name || tp.prefix || '',
            title: tp.title || tp.name || tp.prefix || '',
          }))
      : [];

    // Compat: APIs viejas podían mandar un solo typePeopleId.
    const typePeopleIds =
      typePeople.length > 0
        ? typePeople.map((tp) => tp.id)
        : it?.typePeopleId
          ? [String(it.typePeopleId)]
          : Array.isArray(it?.typePeopleIds)
            ? it.typePeopleIds.map((id: unknown) => String(id))
            : [];

    const typePeopleTitle =
      typePeople
        .map((tp) => tp.title || tp.name || tp.prefix)
        .filter(Boolean)
        .join(', ') ||
      it?.typePeopleTitle ||
      '';

    return {
      id: it?.id,
      title: it?.name || it?.title || '',
      name: it?.name || it?.title || '',
      code: it?.code || '',
      prefix: it?.prefix || '',
      typeOfTaxpayerId: it?.typeOfTaxpayerId
        ? String(it.typeOfTaxpayerId)
        : undefined,
      typeOfTaxpayerPrefix: it?.typeOfTaxpayerPrefix || it?.prefix || '',
      typePeople,
      typePeopleIds,
      typePeopleTitle,
      status: this.normalizeStatus(it?.status),
    };
  }

  private toApiPayload(payload: any): Record<string, unknown> {
    const body: Record<string, unknown> = {
      name: String(payload?.name ?? payload?.title ?? '').trim(),
      code: String(payload?.code ?? '').trim() || undefined,
      prefix: String(payload?.prefix ?? '').trim() || undefined,
      status: payload?.status
        ? String(payload.status).toUpperCase()
        : undefined,
    };
    if (payload?.typePeopleIds !== undefined) {
      body['typePeopleIds'] = payload.typePeopleIds;
    } else if (payload?.typePeopleId != null) {
      body['typePeopleIds'] = [String(payload.typePeopleId)];
    }
    return body;
  }

  createTypeOfTaxpayer(countryId: string, payload: any) {
    const url = `${this.apiUrl}/${countryId}/type-of-taxpayers`;
    return this.httpClient
      .post<{ success: boolean; message: string }>(url, this.toApiPayload(payload))
      .pipe(
        map((res) => ({
          message: res.message || 'Tipo de contribuyente creado',
        })),
      );
  }

  updateTypeOfTaxpayer(countryId: string, id: string | number, payload: any) {
    const url = `${this.apiUrl}/${countryId}/type-of-taxpayers/${id}`;
    return this.httpClient
      .put<{ success: boolean; message: string }>(
        url,
        this.toApiPayload(payload),
      )
      .pipe(
        map((res) => ({
          message: res.message || 'Tipo de contribuyente actualizado',
        })),
      );
  }

  deleteTypeOfTaxpayer(countryId: string, id: string | number) {
    const url = `${this.apiUrl}/${countryId}/type-of-taxpayers/${id}`;
    return this.httpClient
      .delete<{ success: boolean; message: string }>(url)
      .pipe(
        map((res) => ({
          message: res.message || 'Tipo de contribuyente eliminado',
        })),
      );
  }
}

export interface TypeOfTaxpayerPeople {
  id: string;
  prefix?: string;
  name?: string;
  title?: string;
}

export interface TypeOfTaxpayer {
  id: string;
  title: string;
  name?: string;
  prefix?: string;
  code?: string;
  /** Legacy: ya no hay padre global; se conserva por compat UI. */
  typeOfTaxpayerId?: string;
  typeOfTaxpayerPrefix?: string;
  /** Asociaciones de tipo de persona fiscal (respuesta API). */
  typePeople?: TypeOfTaxpayerPeople[];
  /** IDs seleccionados / derivados para el multiselect del drawer. */
  typePeopleIds?: string[];
  /** Label agregado para la columna de la tabla. */
  typePeopleTitle?: string;
  status?: string;
}

export interface TypeOfTaxpayerPageResponse {
  items: TypeOfTaxpayer[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
